import axiosInstance from './axios';

import {
  RegisterPayload,
  LoginPayload,
  MeResponse,
  ChangePasswordPayload,
  Verify2FAPayload,
  TransferPayload,
  RequestVaultTransferPayload,
} from '../types/auth';
import { LoginHistoryResponse } from '../types/loginHistory';
import { getDeviceHeaders } from '../utils/deviceInfo';
import { generateIdempotencyKey } from '../utils/idempotency';

export const authService = {
  fetchCurrentUser: async (): Promise<MeResponse> => {
    const response = await axiosInstance.get('/client/profile');
    return response.data;
  },

  registerUser: async (userData: RegisterPayload) => {
    const headers = await getDeviceHeaders();
    const response = await axiosInstance.post('/client/auth/register', userData, { headers });
    return response.data;
  },

  loginUser: async (credentials: LoginPayload) => {
    const headers = await getDeviceHeaders();
    const response = await axiosInstance.post('/client/auth/login', credentials, { headers });
    return response.data;
  },

  changePassword: async (payload: ChangePasswordPayload) => {
    const response = await axiosInstance.patch('/client/profile/password', payload);
    return response.data;
  },

  updatePrivacy: async (payload: { isUsernameHidden: boolean }) => {
    const response = await axiosInstance.patch('/client/profile/privacy', payload);
    return response.data;
  },

  googleLogin: async (idToken: string) => {
    const headers = await getDeviceHeaders();
    const response = await axiosInstance.post(
      '/client/auth/google/token',
      { idToken },
      { headers },
    );
    return response.data;
  },

  getLoginHistory: async (
    page = 1,
    limit = 10,
    sortField = 'id',
    sortValue = 'DESC',
  ): Promise<LoginHistoryResponse> => {
    const response = await axiosInstance.get(
      `/client/profile/login-history?page=${page}&limit=${limit}&sortField=${sortField}&sortValue=${sortValue}`,
    );
    return response.data;
  },

  logoutUser: async () => {
    const response = await axiosInstance.post('/client/auth/logout');
    return response.data;
  },

  verifyEmail: async (token: string) => {
    const response = await axiosInstance.post('/client/auth/verify-email', { token });
    return response;
  },

  resendVerificationEmail: async () => {
    const response = await axiosInstance.post('/client/auth/resend-verification');
    return response;
  },

  get2FASetup: async () => {
    const response = await axiosInstance.get('/client/auth/2fa/setup');
    return response.data;
  },

  confirm2FA: async (code: string) => {
    const response = await axiosInstance.post('/client/auth/2fa/enable/confirm', { code });
    return response.data;
  },

  disable2FA: async (code: string) => {
    const response = await axiosInstance.post('/client/auth/2fa/disable', { code });
    return response.data;
  },

  verify2FA: async (payload: Verify2FAPayload) => {
    const response = await axiosInstance.post('/client/auth/2fa/verify', payload);
    return response.data;
  },
  fetchMyLevel: async () => {
    const response = await axiosInstance.get('/client/levels/me');
    return response.data;
  },
  spinLevelMember: async (levelKey: string, idempotencyKey?: string) => {
    const key = idempotencyKey || generateIdempotencyKey();
    const response = await axiosInstance.post(
      `/client/levels/spin?levelKey=${levelKey}`,
      {},
      {
        headers: { 'Idempotency-Key': key },
      },
    );
    return response.data;
  },
  getPointCategories: async () => {
    const siteId = process.env.REACT_APP_TENANT_ID || 1;
    const response = await axiosInstance.get(`/client/levels/point-categories?siteId=${siteId}`);
    return response.data;
  },

  transferBalance: async (payload: TransferPayload, idempotencyKey?: string) => {
    const key = payload.idempotencyKey || idempotencyKey || generateIdempotencyKey();
    const response = await axiosInstance.post('/client/balance/transfer', payload, {
      headers: { 'Idempotency-Key': key },
    });
    return response.data;
  },

  /**
   * Step 1 of a vault deposit — no money moves yet. The response says which
   * code the player must enter next: `email` (one sent to `maskedEmail`) or
   * `authenticator` (their 2FA app).
   */
  requestVaultTransfer: async (payload: RequestVaultTransferPayload) => {
    const response = await axiosInstance.post('/client/balance/vault/request', payload);
    return response.data;
  },

  /** Step 2 — submits the approval code and actually performs the transfer. */
  confirmVaultTransfer: async (code: string) => {
    const response = await axiosInstance.post('/client/balance/vault/confirm', { code });
    return response.data;
  },

  getVaultLocks: async (currency?: string) => {
    const query = currency ? `?currency=${currency}` : '';
    const response = await axiosInstance.get(`/client/balance/vault-locks${query}`);
    return response.data;
  },

  claimReferralPayout: async (idempotencyKey?: string) => {
    const key = idempotencyKey || generateIdempotencyKey();
    const response = await axiosInstance.post(
      '/client/referral/payout/claim',
      {},
      {
        headers: { 'Idempotency-Key': key },
      },
    );
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await axiosInstance.post('/client/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (payload: { token: string; newPassword: string }) => {
    const response = await axiosInstance.post('/client/auth/reset-password', payload);
    return response.data;
  },

  getSumsubToken: async () => {
    const response = await axiosInstance.post('/client/kyc/token');
    return response.data;
  },

  fetchBalance: async (): Promise<any> => {
    const response = await axiosInstance.get('/client/balance');
    return response.data;
  },

  requestSelfExclusion: async () => {
    const response = await axiosInstance.post('/client/profile/self-exclusion/request');
    return response.data;
  },

  confirmSelfExclusion: async (code: string) => {
    const response = await axiosInstance.post('/client/profile/self-exclusion/confirm-code', {
      code,
    });
    return response.data;
  },

  resendSelfExclusionCode: async () => {
    const response = await axiosInstance.post('/client/profile/self-exclusion/resend-code');
    return response.data;
  },

  finalizeSelfExclusion: async (until: string) => {
    const response = await axiosInstance.post('/client/profile/self-exclusion/finalize', { until });
    return response.data;
  },

  rejectSelfExclusion: async () => {
    const response = await axiosInstance.post('/client/profile/self-exclusion/reject');
    return response.data;
  },

  updateLanguage: async (language: string) => {
    const response = await axiosInstance.patch('/client/profile/language', { language });
    return response.data;
  },
};
